import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Eye, RotateCw, CheckCheck, ShieldCheck, ExternalLink, Ban } from 'lucide-react';
import { useToast } from '../../../../context/ToastContext';
import { useAppSelector } from '../../../../store/hooks';
import { selectUser } from '../../../../store/selectors';
import {
  fetchDocumentHistory,
  fetchDocumentPdf,
  fetchVersionPdf,
  retryDispatches,
  resolveDispatch,
  revokeReleaseException,
  errorMessage,
  openPendingWindow,
  showBlobIn,
} from '../../../../api/document/documentCentreApi';
import {
  CHANNEL_LABEL,
  RESULT,
  TRAVEL_TYPES,
  formatMoney,
  formatStamp,
  hasPermission,
  canSendDocuments,
} from '../documentCentre.constants';
import ReleaseExceptionModal from './ReleaseExceptionModal';
import s from './DocumentHistoryDrawer.module.css';

const FINANCIAL_BLOCKS = new Set(['outstanding_balance', 'payment_processing', 'credit_overdue', 'exception_reassess']);

/**
 * One document's story (spec §16-§17): why it is or is not released, who it
 * goes to, every version, and every dispatch, print and download.
 */
const DocumentHistoryDrawer = ({ documentId, onClose, onChanged, onOpenBooking }) => {
  const { showToast } = useToast();
  const user = useAppSelector(selectUser);
  const canSend = canSendDocuments(user);
  const canException = hasPermission(user, 'document.release.exception');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState('');
  const [exceptionOpen, setExceptionOpen] = useState(false);
  // Kept in a ref so a new onClose from the parent never reloads the history.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchDocumentHistory(documentId));
    } catch (err) {
      showToast(await errorMessage(err, 'Could not load the document history'), 'error');
      closeRef.current?.();
    } finally {
      setLoading(false);
    }
  }, [documentId, showToast]);

  useEffect(() => {
    load();
  }, [load]);

  const changed = () => {
    load();
    onChanged?.();
  };

  const view = async (version) => {
    const win = openPendingWindow();
    try {
      const blob = version.id ? await fetchVersionPdf(version.id) : await fetchDocumentPdf(documentId, 'view');
      showBlobIn(win, blob);
    } catch (err) {
      win?.close();
      showToast(await errorMessage(err, 'Could not open the document'), 'error');
    }
  };

  const retry = async (row) => {
    setBusy(`retry-${row.id}`);
    try {
      const result = await retryDispatches([row.id]);
      const outcome = result.results?.[0];
      if (outcome?.status === 'sent') showToast('Sent again successfully', 'success');
      else showToast(outcome?.error || 'The retry did not succeed', 'error');
      changed();
    } catch (err) {
      showToast(await errorMessage(err, 'Retry failed'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const submitNote = async () => {
    const { kind, id } = noteFor;
    setBusy(`${kind}-${id}`);
    try {
      if (kind === 'resolve') {
        await resolveDispatch(id, note.trim());
        showToast('Marked as resolved', 'success');
      } else {
        await revokeReleaseException(id, note.trim());
        showToast('Exception revoked', 'success');
      }
      setNoteFor(null);
      setNote('');
      changed();
    } catch (err) {
      showToast(await errorMessage(err, 'Could not save'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const doc = data?.document;
  const financialBlock = doc?.release?.blocks?.some((b) => FINANCIAL_BLOCKS.has(b.code));

  const noteInput = (placeholder) => (
    <div className={s.inline}>
      <input
        className={s.input}
        autoFocus
        placeholder={placeholder}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && note.trim().length >= 3 && submitNote()}
      />
      <button type="button" className={s.btn} disabled={note.trim().length < 3 || busy} onClick={submitNote}>
        Save
      </button>
      <button type="button" className={s.btn} onClick={() => setNoteFor(null)}>
        Cancel
      </button>
    </div>
  );

  return createPortal(
    <>
      <div className={s.overlay} onClick={onClose} />
      <aside className={s.drawer} role="dialog" aria-label="Document history">
        <div className={s.head}>
          <div>
            <div className={s.title}>
              {doc ? `${doc.typeLabel} ${doc.documentNumber || ''}` : 'Document'}
              {doc && <span className={s.tag}>Version {doc.version}</span>}
            </div>
            {doc && (
              <div className={s.sub}>
                {doc.bookingReference}
                {doc.customerName ? `, ${doc.customerName}` : ''}
              </div>
            )}
          </div>
          <button type="button" className={s.close} onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {loading && !data ? (
          <div className={s.loading}>Loading history...</div>
        ) : doc ? (
          <div className={s.body}>
            <div className={s.section}>
              <span className={s.sectionTitle}>Release for customer use</span>
              <div className={s.card}>
                {doc.release.eligible ? (
                  <span className={s.ok}>Eligible</span>
                ) : (
                  <>
                    <span className={s.bad}>Blocked</span>
                    {doc.release.blocks.map((b) => (
                      <span key={`${b.code}-${b.label}`} className={s.reason}>
                        {b.label}
                      </span>
                    ))}
                  </>
                )}
                {doc.release.financial?.released && (
                  <span className={s.line}>{doc.release.financial.label}</span>
                )}
                {(doc.release.warnings || []).map((w) => (
                  <span key={w.code} className={s.warn}>
                    {w.label}
                  </span>
                ))}
                {data.finance && (
                  <span className={s.muted}>
                    Finance: paid {formatMoney(data.finance.paid)}, outstanding {formatMoney(data.finance.outstanding)}
                  </span>
                )}
                <div className={s.btnRow}>
                  {doc.bookingId && (
                    <button type="button" className={`${s.btn} ${s.btnBlue}`} onClick={() => onOpenBooking(doc)}>
                      <ExternalLink size={13} /> Open booking to generate or correct
                    </button>
                  )}
                  {financialBlock && TRAVEL_TYPES.includes(doc.subtype) && canException && (
                    <button type="button" className={`${s.btn} ${s.btnOrange}`} onClick={() => setExceptionOpen(true)}>
                      <ShieldCheck size={13} /> Release despite balance
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className={s.section}>
              <span className={s.sectionTitle}>Recipient for this document</span>
              <div className={s.card}>
                <span className={s.line}>
                  {doc.recipient.roleLabel}: {doc.recipient.name || '-'}
                </span>
                <span className={s.muted}>Email: {doc.recipient.email || 'missing'}</span>
                <span className={s.muted}>Mobile: {doc.recipient.phone || 'missing'}</span>
                <span className={s.muted}>Language: {doc.recipient.language ? doc.recipient.language.toUpperCase() : 'not recorded'}</span>
              </div>
            </div>

            <div className={s.section}>
              <span className={s.sectionTitle}>Versions</span>
              {data.versions.map((v) => (
                <div key={v.id || 'current'} className={`${s.card} ${s.row}`}>
                  <div>
                    <span className={s.line}>
                      Version {v.version} {v.current ? '(current)' : ''}
                    </span>
                    {v.supersededAt && <span className={s.muted}> Replaced {formatStamp(v.supersededAt)}</span>}
                  </div>
                  <button type="button" className={`${s.btn} ${s.btnBlue}`} onClick={() => view(v)}>
                    <Eye size={13} /> View
                  </button>
                </div>
              ))}
            </div>

            <div className={s.section}>
              <span className={s.sectionTitle}>Dispatches, prints and downloads</span>
              {!data.dispatches.length && <span className={s.muted}>Nothing processed yet.</span>}
              {data.dispatches.map((d) => {
                const meta = RESULT[d.status] || { label: d.status, color: '#475569' };
                const failedOpen = d.status === 'failed' && !d.resolved_at;
                return (
                  <div
                    key={d.id}
                    className={`${s.timelineItem} ${failedOpen ? s.timelineFailed : ''} ${['sent', 'share_started'].includes(d.status) ? s.timelineOk : ''}`}
                  >
                    <span className={s.line}>
                      <strong>{CHANNEL_LABEL[d.channel] || d.channel}</strong>
                      {d.purpose === 'internal' ? ' (internal copy)' : ''}{' '}
                      <span style={{ color: meta.color, fontWeight: 700 }}>{meta.label}</span>
                      {!d.isDispatch && <span className={s.muted}>, not a dispatch</span>}
                    </span>
                    <span className={s.muted}>
                      {formatStamp(d.completed_at || d.created_at)}, version {d.document_version}
                      {d.staffName ? `, by ${d.staffName}` : ''}
                    </span>
                    {d.recipient_address && (
                      <span className={s.muted}>
                        To {d.recipient_name ? `${d.recipient_name}, ` : ''}
                        {d.recipient_address}
                        {d.is_intended ? '' : ' (not the booking contact)'}
                      </span>
                    )}
                    {d.release_note && <span className={s.muted}>Released: {d.release_note}</span>}
                    {d.error_message && <span className={s.reason}>{d.error_message}</span>}
                    {d.resolved_at && (
                      <span className={s.muted}>
                        Resolved {formatStamp(d.resolved_at)}
                        {d.resolvedByName ? ` by ${d.resolvedByName}` : ''}: {d.resolution_note}
                      </span>
                    )}
                    {failedOpen && canSend && (
                      <div className={s.btnRow}>
                        {d.channel === 'email' && (
                          <button type="button" className={s.btn} disabled={!!busy} onClick={() => retry(d)}>
                            <RotateCw size={13} /> Retry
                          </button>
                        )}
                        <button
                          type="button"
                          className={s.btn}
                          onClick={() => {
                            setNoteFor({ kind: 'resolve', id: d.id });
                            setNote('');
                          }}
                        >
                          <CheckCheck size={13} /> Mark resolved
                        </button>
                      </div>
                    )}
                    {noteFor?.kind === 'resolve' && noteFor.id === d.id && noteInput('How was it resolved?')}
                  </div>
                );
              })}
            </div>

            {data.exceptions.length > 0 && (
              <div className={s.section}>
                <span className={s.sectionTitle}>Financial exceptions on this booking</span>
                {data.exceptions.map((e) => (
                  <div key={e.id} className={s.card}>
                    <span className={s.line}>
                      {e.revokedAt ? 'Revoked' : 'Active'}: {e.reason}
                    </span>
                    <span className={s.muted}>
                      Granted {formatStamp(e.grantedAt)}
                      {e.grantedBy ? ` by ${e.grantedBy}` : ''} at an outstanding balance of {formatMoney(e.outstandingAtGrant)}
                    </span>
                    {e.revokedAt && (
                      <span className={s.muted}>
                        Revoked {formatStamp(e.revokedAt)}
                        {e.revokedBy ? ` by ${e.revokedBy}` : ''}: {e.revokeReason}
                      </span>
                    )}
                    {!e.revokedAt && canException && (
                      <div className={s.btnRow}>
                        <button
                          type="button"
                          className={s.btn}
                          onClick={() => {
                            setNoteFor({ kind: 'revoke', id: e.id });
                            setNote('');
                          }}
                        >
                          <Ban size={13} /> Revoke
                        </button>
                      </div>
                    )}
                    {noteFor?.kind === 'revoke' && noteFor.id === e.id && noteInput('Why is it revoked?')}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </aside>
      {exceptionOpen && doc && (
        <ReleaseExceptionModal
          open
          bookingReference={doc.bookingReference}
          outstanding={doc.outstanding}
          subtypes={[doc.subtype]}
          onClose={() => setExceptionOpen(false)}
          onGranted={changed}
        />
      )}
    </>,
    document.body
  );
};

export default DocumentHistoryDrawer;
